import { useEffect, useState } from 'react'
import { useParams, useNavigate, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SUPPORTED_LANGUAGES, type SupportedLanguage, detectLocale } from '@/lib/i18n'

export function LanguageProvider() {
  const { lang } = useParams<{ lang: string }>()
  const navigate = useNavigate()
  const { i18n } = useTranslation()
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    const browserLang = navigator.language
    let targetLang = 'en'

    // If we're at the root path (no lang param)
    if (!lang) {
      targetLang = detectLocale(browserLang)
    }
    // If we have an explicit language in the URL
    else if (SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage)) {
      targetLang = lang
    }
    // If we have an unsupported language in the URL, redirect based on browser preference
    else {
      const defaultLang = detectLocale(browserLang)
      const currentPath = window.location.pathname.replace(/^\/[^/]*/, '')
      const redirectLang = defaultLang === 'en' ? '' : defaultLang
      navigate(`/${redirectLang}${currentPath}`.replace('//', '/'), { replace: true })
      return
    }

    // Set language synchronously if it's different
    if (i18n.language !== targetLang) {
      i18n.changeLanguage(targetLang).then(() => setIsReady(true))
    } else {
      setIsReady(true)
    }
  }, [lang, navigate, i18n])

  // Don't render anything until language is ready
  if (!isReady) {
    return null
  }

  return <Outlet />
}